import React from 'react';
import { Alert, Button, Form, Input, InputNumber, Table, Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from '../../redux/slices/appInfo';
import {
    syncBillingStatus,
    createCheckoutSession,
    createPortalSession,
    getInvoices,
    getBillingSettings,
    updateBillingSettings,
    getBillingHistory,
} from '../../api/billing';
import {
    BillingHistoryEntry,
    BillingSettings,
    BillingStatus,
    Invoice,
} from '../../api/billing/type';
import { useNotification } from '../../hooks/useNotification';
import { isPlatformAdmin } from '../../utils/constants';
import { RootState } from '../../redux/reducers';

const STATUS_LABELS: Record<string, string> = {
    trialing: 'Trial active',
    active: 'Active',
    past_due: 'Past due',
    incomplete: 'Payment pending',
    canceled: 'Canceled',
};

const INVOICE_STATUS_COLORS: Record<string, string> = {
    paid: 'green',
    open: 'orange',
    draft: 'default',
    void: 'default',
    uncollectible: 'red',
};

const PANEL = 'bg-white rounded-[8px] p-[24px]';

const formatTrialLabel = (trialDays: number) => {
    if (trialDays <= 0) return 'no free trial';
    if (trialDays % 30 === 0 && trialDays >= 30) {
        const months = trialDays / 30;
        return months === 1
            ? '1-month free trial'
            : `${months}-month free trial`;
    }
    return `${trialDays}-day free trial`;
};

export const BillingPage: React.FC = () => {
    const dispatch = useDispatch();
    const { showError, showSuccess } = useNotification();
    const userInfo = useSelector((state: RootState) => state.userInfo);
    const canManagePlanSettings = isPlatformAdmin(userInfo?.roles);
    const [status, setStatus] = React.useState<BillingStatus | null>(null);
    const [settings, setSettings] = React.useState<BillingSettings | null>(
        null
    );
    const [invoices, setInvoices] = React.useState<Invoice[]>([]);
    const [changeLog, setChangeLog] = React.useState<BillingHistoryEntry[]>([]);
    const [fetching, setFetching] = React.useState(true);
    const [savingSettings, setSavingSettings] = React.useState(false);
    const [settingsForm] = Form.useForm<BillingSettings>();

    const load = React.useCallback(async () => {
        try {
            setFetching(true);
            dispatch(setLoading(true));
            const res = await syncBillingStatus();
            setStatus(res.data);
            try {
                const inv = await getInvoices();
                setInvoices(inv.data);
            } catch {
                setInvoices([]);
            }
            if (canManagePlanSettings) {
                try {
                    const cfg = await getBillingSettings();
                    setSettings(cfg.data);
                    settingsForm.setFieldsValue({
                        pricePerVehicle: cfg.data.pricePerVehicle,
                        trialDays: cfg.data.trialDays,
                        stripePriceId: cfg.data.stripePriceId ?? '',
                    });
                } catch (err: unknown) {
                    const detail = (
                        err as { response?: { data?: { detail?: string } } }
                    )?.response?.data?.detail;
                    showError(
                        'Billing',
                        detail ?? 'Failed to load plan settings'
                    );
                }
                try {
                    const h = await getBillingHistory(50);
                    setChangeLog(h.data);
                } catch {
                    setChangeLog([]);
                }
            }
        } catch {
            showError('Billing', 'Failed to load billing status');
        } finally {
            setFetching(false);
            dispatch(setLoading(false));
        }
    }, [canManagePlanSettings, dispatch, settingsForm, showError]);

    React.useEffect(() => {
        load();
    }, [load]);

    const redirectToStripe = async (
        request: () => Promise<{ data?: { url?: string } }>,
        errorMessage: string
    ) => {
        try {
            dispatch(setLoading(true));
            const res = await request();
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                showError('Billing', errorMessage);
            }
        } catch {
            showError('Billing', errorMessage);
        } finally {
            dispatch(setLoading(false));
        }
    };

    const onStartTrial = () =>
        redirectToStripe(createCheckoutSession, 'Failed to start checkout');

    const onManageBilling = () =>
        redirectToStripe(createPortalSession, 'Failed to open billing portal');

    const onSaveSettings = async (values: BillingSettings) => {
        try {
            setSavingSettings(true);
            const res = await updateBillingSettings({
                pricePerVehicle: values.pricePerVehicle,
                trialDays: values.trialDays,
                stripePriceId: values.stripePriceId?.trim() || null,
            });
            setSettings(res.data);
            showSuccess('Billing', 'Plan settings saved');
            await load();
        } catch (err: unknown) {
            const detail = (
                err as { response?: { data?: { detail?: string } } }
            )?.response?.data?.detail;
            showError('Billing', detail ?? 'Failed to save plan settings');
        } finally {
            setSavingSettings(false);
        }
    };

    if (fetching) return null;

    const s = status?.status ?? 'none';
    const hasLiveSub = s === 'active' || s === 'trialing' || s === 'past_due';
    const pricePerVehicle =
        status?.pricePerVehicle ?? settings?.pricePerVehicle ?? 20;
    const trialDays = status?.trialDays ?? settings?.trialDays ?? 30;
    const trialLabel = formatTrialLabel(trialDays);

    const subscriptionPanel = (
        <div className={PANEL}>
            {!hasLiveSub ? (
                <>
                    <h2 className="text-heading-6 font-semibold m-0">
                        AutoTrack Car Subscription
                    </h2>
                    <p className="text-body text-mid-em mt-2 mb-5">
                        ${pricePerVehicle.toFixed(2)} / car / month &mdash;{' '}
                        {s === 'canceled' ? '' : `${trialLabel}, `}card required
                    </p>
                    <Button type="primary" size="large" onClick={onStartTrial}>
                        {s === 'canceled'
                            ? 'Re-subscribe at new price'
                            : trialDays > 0
                              ? 'Start free trial'
                              : 'Subscribe'}
                    </Button>
                </>
            ) : (
                <>
                    <h2 className="text-heading-6 font-semibold m-0">
                        {STATUS_LABELS[s] ?? 'Canceled'}
                    </h2>
                    <div className="text-body mt-3 flex flex-col gap-1">
                        <span>
                            Cars: {status?.carCount ?? 0} of{' '}
                            {status?.quantity ?? 0} paid slots
                        </span>
                        {!status?.cancelAtPeriodEnd &&
                            (status?.carCount ?? 0) >=
                                (status?.quantity ?? 0) && (
                                <span className="text-red-500">
                                    All slots in use — increase the quantity in
                                    Manage billing to add more cars.
                                </span>
                            )}
                        {status?.cancelAtPeriodEnd && (
                            <span className="text-amber-600">
                                This plan is scheduled to end
                                {status?.cancelAt
                                    ? ` on ${new Date(status.cancelAt).toLocaleDateString()}`
                                    : ''}
                                . Slot changes are disabled on the old price —
                                re-subscribe at the new price after it ends to
                                manage slots again.
                            </span>
                        )}
                        <span>
                            Monthly total: $
                            {(status?.monthlyAmount ?? 0).toFixed(2)} (
                            {status?.quantity ?? 0} × $
                            {pricePerVehicle.toFixed(2)})
                        </span>
                        {status?.trialEndsAt && (
                            <span className="text-mid-em">
                                Trial ends:{' '}
                                {new Date(
                                    status.trialEndsAt
                                ).toLocaleDateString()}
                            </span>
                        )}
                        {status?.currentPeriodEnd && (
                            <span className="text-mid-em">
                                Next billing:{' '}
                                {new Date(
                                    status.currentPeriodEnd
                                ).toLocaleDateString()}
                            </span>
                        )}
                    </div>
                    <Button
                        type="primary"
                        className="mt-5"
                        onClick={onManageBilling}
                    >
                        Manage billing
                    </Button>
                </>
            )}
        </div>
    );

    const settingsPanel = (
        <div className={PANEL}>
            <h2 className="text-heading-6 font-semibold m-0 mb-1">
                Plan settings
            </h2>
            <p className="text-body text-mid-em mt-0 mb-4">
                Global pricing and trial period stored in the database. New
                checkouts use these values. Price per vehicle must match the
                Stripe Price unit amount (validated on save).
            </p>
            <Form
                form={settingsForm}
                layout="vertical"
                onFinish={onSaveSettings}
                className="max-w-[420px]"
            >
                <Form.Item
                    name="pricePerVehicle"
                    label="Price per vehicle (USD / month)"
                    rules={[
                        { required: true, message: 'Price is required' },
                        {
                            type: 'number',
                            min: 0.01,
                            message: 'Price must be greater than zero',
                        },
                    ]}
                >
                    <InputNumber
                        min={0.01}
                        step={1}
                        precision={2}
                        prefix="$"
                        className="w-full"
                    />
                </Form.Item>
                <Form.Item
                    name="trialDays"
                    label="Trial period (days)"
                    rules={[
                        { required: true, message: 'Trial days is required' },
                        {
                            type: 'number',
                            min: 0,
                            message: 'Trial days cannot be negative',
                        },
                    ]}
                >
                    <InputNumber min={0} step={1} className="w-full" />
                </Form.Item>
                <Form.Item
                    name="stripePriceId"
                    label="Stripe Price ID"
                    extra="Optional if Stripe:PriceId is set in server config."
                >
                    <Input placeholder="price_..." />
                </Form.Item>
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={savingSettings}
                >
                    Save plan settings
                </Button>
            </Form>
        </div>
    );

    const historyPanel = (
        <div className={PANEL}>
            <h2 className="text-heading-6 font-semibold m-0 mb-4">
                Billing history
            </h2>
            <Table<Invoice>
                rowKey={r => r.number || r.date}
                dataSource={invoices}
                pagination={false}
                size="small"
                locale={{ emptyText: 'No invoices yet' }}
                columns={[
                    {
                        title: 'Date',
                        dataIndex: 'date',
                        render: (d: string) => new Date(d).toLocaleDateString(),
                    },
                    {
                        title: 'Amount',
                        dataIndex: 'amount',
                        render: (a: number) => `$${a.toFixed(2)}`,
                    },
                    {
                        title: 'Cars',
                        dataIndex: 'quantity',
                    },
                    {
                        title: 'Status',
                        dataIndex: 'status',
                        render: (st: string) => (
                            <Tag color={INVOICE_STATUS_COLORS[st] ?? 'default'}>
                                {st}
                            </Tag>
                        ),
                    },
                    {
                        title: 'Invoice',
                        key: 'invoice',
                        render: (_, r) =>
                            r.pdfUrl || r.invoiceUrl ? (
                                <a
                                    href={(r.pdfUrl || r.invoiceUrl) as string}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    View
                                </a>
                            ) : (
                                '—'
                            ),
                    },
                ]}
            />
        </div>
    );

    const changeLogPanel = (
        <div className={PANEL}>
            <h2 className="text-heading-6 font-semibold m-0 mb-4">
                Change log
            </h2>
            <Table<BillingHistoryEntry>
                rowKey={r => String(r.billingHistoryId)}
                dataSource={changeLog}
                pagination={{ pageSize: 10 }}
                size="small"
                locale={{ emptyText: 'No changes yet' }}
                columns={[
                    {
                        title: 'When',
                        dataIndex: 'createdAt',
                        render: (d: string) => new Date(d).toLocaleString(),
                    },
                    {
                        title: 'Action',
                        dataIndex: 'action',
                        render: (a: string) => (
                            <Tag>{a.replace(/_/g, ' ')}</Tag>
                        ),
                    },
                    {
                        title: 'Org',
                        dataIndex: 'organizationId',
                        render: (o: number | null) => o ?? '—',
                    },
                    {
                        title: 'Price',
                        key: 'price',
                        render: (_, r) =>
                            r.oldUnitAmount != null || r.newUnitAmount != null
                                ? `${r.oldUnitAmount != null ? '$' + r.oldUnitAmount.toFixed(2) : '—'} → ${r.newUnitAmount != null ? '$' + r.newUnitAmount.toFixed(2) : '—'}`
                                : '—',
                    },
                    {
                        title: 'Qty',
                        dataIndex: 'quantity',
                        render: (q: number | null) => q ?? '—',
                    },
                    {
                        title: 'Note',
                        dataIndex: 'note',
                        render: (n: string | null) => n ?? '—',
                    },
                ]}
            />
        </div>
    );

    return (
        <div className="w-full p-[24px] flex flex-col gap-[24px]">
            {s === 'past_due' && (
                <Alert
                    type="error"
                    message="Payment overdue"
                    description="Update your payment method to avoid losing access."
                    showIcon
                    action={
                        <Button danger onClick={onManageBilling}>
                            Update payment
                        </Button>
                    }
                />
            )}

            {status?.actionRequired && s !== 'none' && s !== 'past_due' && (
                <Alert
                    type={status.cancelAtPeriodEnd ? 'warning' : 'error'}
                    message={
                        status.cancelAtPeriodEnd
                            ? 'Plan scheduled to end'
                            : 'Subscription required'
                    }
                    description={
                        status.actionMessage ??
                        'Re-subscribe at the new price to keep using the system.'
                    }
                    showIcon
                    action={
                        status.cancelAtPeriodEnd ? undefined : (
                            <Button type="primary" onClick={onStartTrial}>
                                Re-subscribe at new price
                            </Button>
                        )
                    }
                />
            )}

            {s === 'none' ? (
                <div className="max-w-[520px] mx-auto w-full">
                    {subscriptionPanel}
                </div>
            ) : (
                <div className="flex gap-[24px] items-start max-lg:flex-col">
                    <div className="w-full lg:w-[36%]">{subscriptionPanel}</div>
                    <div className="w-full lg:flex-1">{historyPanel}</div>
                </div>
            )}

            {canManagePlanSettings && settingsPanel}

            {canManagePlanSettings && changeLogPanel}
        </div>
    );
};
